import type { SourceRecords } from "./materialize";
import { materialize } from "./materialize";
import { createTehoRecords } from "./teho-data";
import { createNcleanData } from "./nclean-data";
export const OU_ID = "north-coast";
export const DEMO_DATE = "2026-10-06";
export const DEMO_TIME = "13:20";
export const DEMO_START = "2026-07-09";
export const AS_OF = "2026-10-06T10:20:00.000Z";
export const MODEL_VERSION = "luuta-malloy-1";
export const STORY_ASSIGNMENT = "task-89-0-0";
export const PROPERTIES = [
  { id: "north-coast", name: "North Coast Resort", short: "North Coast" },
  { id: "pine-lake", name: "Pine Lake Retreat", short: "Pine Lake" },
  { id: "harbour", name: "Harbour House", short: "Harbour House" },
];
const NAMES = [
  "Emma Virtanen",
  "Lina Korhonen",
  "Amir Hassan",
  "Sofia Laine",
  "Noah Niemi",
  "Anna Koski",
  "Elias Aalto",
  "Sara Lehto",
  "Leo Mäkinen",
  "Mila Salonen",
  "Oliver Berg",
  "Aino Ranta",
];
export function dateOffset(days: number): string {
  const date = new Date(DEMO_DATE + "T12:00:00Z");
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
// All fixture dates fall within Helsinki daylight-saving time (UTC+3).
const stamp = (date: string, localMinutes: number) =>
  new Date(
    Date.parse(date + "T00:00:00Z") + (localMinutes - 180) * 60000,
  ).toISOString();
export function createSourceRecords(): SourceRecords {
  const records: SourceRecords = {
    teho: createTehoRecords(PROPERTIES, OU_ID, dateOffset),
    properties: PROPERTIES.map((p) => ({
      ...p,
      operatingUnitId: OU_ID,
      timezone: "Europe/Helsinki",
      archivedAt: null,
    })),
    units: [],
    tasks: [],
    sessions: [],
    breaks: [],
    inspections: [],
    orders: [],
    operatingUnitTimezone: "Europe/Helsinki",
  };
  PROPERTIES.forEach((property, p) => {
    for (let u = 0; u < 45; u++)
      records.units.push({
        id: `unit-${p}-${u}`,
        propertyId: property.id,
        name: `${["NC", "PL", "HH"][p]}-${String(u + 1).padStart(3, "0")}`,
        unitType: ["Studio", "Two-bedroom", "Family house"][u % 3],
        cleaningState:
          u % 13 === 0
            ? "INSPECTION"
            : u % 11 === 0
              ? "DIRTY"
              : u % 7 === 0
                ? "IN_PROGRESS"
                : "CLEAN",
        archivedAt: u === 44 ? "2026-09-01T00:00:00Z" : null,
        stateChangedAt: stamp(DEMO_DATE, 720 + u),
      });
    for (let d = 0; d < 90; d++) {
      const date = dateOffset(d - 89);
      for (let n = 0; n < 15; n++) {
        const seq = d * 45 + p * 15 + n,
          id = `task-${d}-${p}-${n}`,
          unit = records.units[p * 45 + ((d * 15 + n) % 45)];
        const start = 480 + (n % 5) * 45,
          span = 35 + (seq % 7) * 8,
          historical = d < 89;
        const cancelled = seq % 41 === 0;
        const blocked = !historical && n % 5 === 0;
        const open = !historical && n % 4 === 1;
        const completion = historical && seq % 19 === 0 ? 870 : start + span;
        const status = cancelled
          ? "CANCELLED"
          : blocked
            ? "BLOCKED"
            : open
              ? "IN_PROGRESS"
              : "COMPLETED";
        records.tasks.push({
          id,
          shiftId: `shift-${d}-${p}`,
          propertyId: property.id,
          propertyUnitId: unit.id,
          status,
          startsAt: stamp(date, start),
          shiftStartsAt: stamp(date, 480),
          createdAt: stamp(date, 420),
          actualStartedAt: cancelled ? null : stamp(date, start),
          completedAt:
            status === "COMPLETED"
              ? seq % 173 === 0
                ? null
                : stamp(date, completion)
              : null,
          deadline: seq % 31 === 0 ? null : stamp(date, 840),
          supplyState: blocked ? "sent" : "stocked",
        });
        if (!cancelled)
          for (let person = 0; person < (seq % 4 === 0 ? 2 : 1); person++) {
            const employee = (seq + person) % NAMES.length,
              employeeId = `employee-${employee}`;
            records.sessions.push({
              taskId: id,
              employeeId,
              employeeName: NAMES[employee],
              assignmentId: `assignment-${d}-${p}-${employee}`,
              status: open || blocked ? "active" : "completed",
              startedAt: stamp(date, start),
              endedAt:
                open || blocked
                  ? null
                  : seq % 211 === 0
                    ? stamp(date, start - 2)
                    : stamp(date, completion),
            });
            if (seq % 3 === 0)
              records.breaks.push({
                id: `break-${id}-${person}`,
                taskId: id,
                employeeId,
                type: seq % 2 === 0 ? "coffee1" : "meal",
                startedAt: stamp(date, start + 10),
                endedAt: stamp(date, start + 20),
                durationSeconds: 900,
              });
          }
        if (status === "COMPLETED" && seq % 3 !== 1) {
          const failed = seq % 9 === 0,
            submitted = stamp(date, completion + 5);
          records.inspections.push({
            id: `inspection-${id}`,
            propertyId: property.id,
            propertyUnitId: unit.id,
            sourceCleanerTaskId: id,
            sourceResultId: null,
            status: "REVIEWED",
            createdAt: stamp(date, completion),
            result: {
              id: `result-${id}`,
              outcome: failed ? "DIRTY" : "CLEAN",
              rating: failed ? 2 : 5,
              submittedAt: submitted,
            },
          });
          if (failed)
            records.inspections.push({
              id: `reinspection-${id}`,
              propertyId: property.id,
              propertyUnitId: unit.id,
              sourceCleanerTaskId: id,
              sourceResultId: `result-${id}`,
              status: "REVIEWED",
              createdAt: stamp(date, completion + 20),
              result: {
                id: `second-result-${id}`,
                outcome: "CLEAN",
                rating: 5,
                submittedAt: stamp(date, completion + 30),
              },
            });
        }
        if (seq % 6 === 0 || blocked)
          for (let order = 0; order < (seq % 12 === 0 ? 2 : 1); order++)
            records.orders.push({
              id: `order-${id}-${order}`,
              operatingUnitId: OU_ID,
              propertyId: property.id,
              propertyUnitId: unit.id,
              taskId: id,
              status: historical
                ? "fulfilled"
                : blocked
                  ? "submitted"
                  : "fulfilled",
              priority: blocked ? "urgent" : "regular",
              createdAt: stamp(date, start + 15 + order * 5),
            });
      }
    }
  });
  // Unlinked operational records exercise nullable task relationships.
  records.orders.push({
    id: "unlinked-order",
    operatingUnitId: OU_ID,
    propertyId: "harbour",
    propertyUnitId: null,
    taskId: null,
    status: "submitted",
    priority: "regular",
    createdAt: stamp(DEMO_DATE, 700),
  });
  return records;
}
export const createDemoData = () => ({
  ...materialize(createSourceRecords(), AS_OF),
  ...createNcleanData(),
});
